/**
 * Video Segment Tracker
 * Tracks actual watch time in 5-second segments for learning analytics
 */

export type VideoSegment = {
  start: number;        // Start time in seconds
  end: number;          // End time in seconds  
  duration: number;     // Actual duration watched in seconds
  isComplete: boolean;  // Whether segment was fully watched (5 seconds)
  reason: string;       // Reason why segment was finalized
  timestamp: number;    // When segment was created
  quality?: string;     // Video quality (e.g., "720p", "1080p")
  speed?: number;       // Playback speed (e.g., 1.0, 1.25, 2.0)
};

export type LogFunction = (message: string, payload?: any, level?: 'info' | 'warn' | 'error' | 'debug', source?: string) => void;

export type SegmentTrackerOptions = {
  segmentSize?: number;     // Segment size in seconds (default: 5)
  maxSegments?: number;     // Max segments to keep in memory (default: 100)
  flushInterval?: number;   // Auto-flush interval in ms (default: 30000)
  logger?: LogFunction;     // Logger function for debugging
  onSegmentCompleted?: (segment: VideoSegment) => void; // Callback when a segment is completed
  getCurrentQuality?: () => string | undefined; // Function to get current video quality
  getCurrentSpeed?: () => number | undefined;   // Function to get current playback speed
};

export class SegmentTracker {
  private currentSegment: VideoSegment | null = null;
  private segments: VideoSegment[] = [];
  private lastKnownTime: number = 0;
  private segmentSize: number;
  private maxSegments: number;
  private flushInterval: number;
  private flushTimer: NodeJS.Timeout | null = null;
  private isPaused: boolean = false;
  private logger: LogFunction;
  private onSegmentCompleted?: (segment: VideoSegment) => void;
  private getCurrentQuality?: () => string | undefined;
  private getCurrentSpeed?: () => number | undefined;

  constructor(options: SegmentTrackerOptions = {}) {
    this.segmentSize = options.segmentSize || 10;
    this.maxSegments = options.maxSegments || 100;
    this.flushInterval = options.flushInterval || 30000;
    this.logger = options.logger || (() => {}); // Default to no-op function
    this.onSegmentCompleted = options.onSegmentCompleted;
    this.getCurrentQuality = options.getCurrentQuality;
    this.getCurrentSpeed = options.getCurrentSpeed;
  }

  /**
   * Start tracking a new segment
   */
  startSegment(startTime: number): void {
    this.finalizeCurrentSegment("starting new segment");
    
    this.currentSegment = {
      start: startTime,
      end: startTime,
      duration: 0,
      isComplete: false,
      reason: "",
      timestamp: Date.now(),
      quality: this.getCurrentQuality?.(),
      speed: this.getCurrentSpeed?.(),
    };
    
    this.lastKnownTime = startTime;
    this.isPaused = false;
    
    this.logger(`Started new segment at ${startTime.toFixed(2)}s`, { segment: this.currentSegment }, 'info', 'SegmentTracker');
  }

  /**
   * Update current segment with new time
   */
  updateSegment(currentTime: number): void {
    if (!this.currentSegment) {
      this.logger(`updateSegment called but no current segment exists at time ${currentTime.toFixed(2)}s`, null, 'warn', 'SegmentTracker');
      return;
    }
    
    if (this.isPaused) {
      this.logger(`updateSegment called but video is paused at time ${currentTime.toFixed(2)}s`, null, 'debug', 'SegmentTracker');
      return;
    }

    // Check if this is a seek (time jump)
    if (this.isSeek(currentTime)) {
      this.logger(`Seek detected in updateSegment: ${this.currentSegment.start + this.currentSegment.duration} -> ${currentTime}`, null, 'info', 'SegmentTracker');
      this.finalizeCurrentSegment("seek detected in updateSegment");
      this.startSegment(currentTime);
      return;
    }

    // Only update if we've actually progressed in time (not paused or same time)
    if (currentTime > this.lastKnownTime) {
      // Calculate the time difference from last known time
      const timeDelta = currentTime - this.lastKnownTime;
      
      // Update segment duration by adding the time delta
      this.currentSegment.duration += timeDelta;
      this.currentSegment.end = currentTime;
      this.lastKnownTime = currentTime;

      // Debug logging (only every 1 second to reduce noise)
      if (Math.floor(this.currentSegment.duration) % 1 === 0 && this.currentSegment.duration > 0) {
        this.logger(`Segment update: start=${this.currentSegment.start.toFixed(2)}, end=${this.currentSegment.end.toFixed(2)}, duration=${this.currentSegment.duration.toFixed(2)}`, { segment: this.currentSegment }, 'debug', 'SegmentTracker');
      }

      // Check if we've reached a 5-second boundary
      if (this.currentSegment.duration >= this.segmentSize) {
        this.logger(`Segment completed: ${this.currentSegment.duration.toFixed(2)}s`, { segment: this.currentSegment }, 'info', 'SegmentTracker');
        this.finalizeCurrentSegment("5-second boundary reached");
        
        // Start new segment at the current time
        this.currentSegment = {
          start: currentTime,
          end: currentTime,
          duration: 0,
          isComplete: false,
          reason: "",
          timestamp: Date.now(),
        };
        this.isPaused = false;
        this.logger(`Started new segment at ${currentTime.toFixed(2)}s`, { segment: this.currentSegment }, 'info', 'SegmentTracker');
      }
    } else if (currentTime < this.lastKnownTime) {
      // Time went backward - this indicates a seek
      this.logger(`Time went backward: ${this.lastKnownTime.toFixed(2)}s -> ${currentTime.toFixed(2)}s (seek detected)`, null, 'warn', 'SegmentTracker');
      this.finalizeCurrentSegment("time went backward (seek)");
      this.startSegment(currentTime);
    }
  }

  /**
   * Handle pause event
   */
  onPause(currentTime: number): void {
    this.logger(`Segment tracker: onPause called at ${currentTime.toFixed(2)}s`, { currentTime }, 'info', 'SegmentTracker');
    this.isPaused = true;
    this.updateSegment(currentTime);
  }

  /**
   * Handle play event
   */
  onPlay(currentTime: number): void {
    this.logger(`Segment tracker: onPlay called at ${currentTime.toFixed(2)}s`, { currentTime }, 'info', 'SegmentTracker');
    this.isPaused = false;
    // Don't start new segment, continue current one
    this.lastKnownTime = currentTime;
  }

  /**
   * Handle seek event (progress bar, keyboard shortcuts, etc.)
   */
  onSeek(fromTime: number, toTime: number): void {
    this.logger(`Seek detected: from ${fromTime.toFixed(2)}s to ${toTime.toFixed(2)}s`, { fromTime, toTime }, 'info', 'SegmentTracker');
    
    // Finalize current segment before seeking
    this.finalizeCurrentSegment("explicit seek event");
    
    // Start new segment at seek position
    this.startSegment(toTime);
  }

  /**
   * Check if a time change indicates a seek (not normal playback)
   */
  private isSeek(currentTime: number): boolean {
    if (!this.currentSegment) return false;
    
    const expectedTime = this.currentSegment.start + this.currentSegment.duration;
    const timeDiff = Math.abs(currentTime - expectedTime);
    
    // If the difference is more than 0.5 seconds, it's likely a seek
    return timeDiff > 0.5;
  }

  /**
   * Handle video end
   */
  onEnd(): void {
    this.finalizeCurrentSegment("video ended");
  }

  /**
   * Finalize current segment and add to segments array
   */
  private finalizeCurrentSegment(reason: string = "unknown"): void {
    if (!this.currentSegment) return;

    // Mark as complete if duration is close to segment size
    this.currentSegment.isComplete = this.currentSegment.duration >= (this.segmentSize * 0.8);
    this.currentSegment.reason = reason;
    
    this.logger(`Finalizing segment: start=${this.currentSegment.start.toFixed(2)}, end=${this.currentSegment.end.toFixed(2)}, duration=${this.currentSegment.duration.toFixed(2)}, complete=${this.currentSegment.isComplete} [REASON: ${reason}]`, { segment: this.currentSegment, reason }, 'info', 'SegmentTracker');
    
    // Trigger callback for completed segments immediately
    if (this.currentSegment.isComplete && this.onSegmentCompleted) {
      this.onSegmentCompleted(this.currentSegment);
    } else {
      // Store incomplete segments for later flushing
      this.segments.push(this.currentSegment);
    }
    
    this.currentSegment = null;

    // Prevent memory leaks by limiting segments
    if (this.segments.length > this.maxSegments) {
      this.segments = this.segments.slice(-this.maxSegments);
    }
  }

  /**
   * Get all completed segments and clear the buffer
   */
  flushSegments(): VideoSegment[] {
    this.finalizeCurrentSegment("flushing segments");
    const segments = [...this.segments];
    this.segments = [];
    return segments;
  }

  /**
   * Flush only completed segments (5-second segments) immediately
   */
  flushCompletedSegments(): VideoSegment[] {
    const completedSegments = this.segments.filter(segment => segment.isComplete);
    this.segments = this.segments.filter(segment => !segment.isComplete);
    return completedSegments;
  }

  /**
   * Get current segments without clearing (for debugging)
   */
  getCurrentSegments(): VideoSegment[] {
    return [...this.segments];
  }

  /**
   * Get current segment info (for debugging)
   */
  getCurrentSegmentInfo(): VideoSegment | null {
    return this.currentSegment ? { ...this.currentSegment } : null;
  }

  /**
   * Start auto-flush timer
   */
  startAutoFlush(onFlush: (segments: VideoSegment[]) => void): void {
    this.stopAutoFlush();
    
    this.flushTimer = setInterval(() => {
      const segments = this.flushSegments();
      if (segments.length > 0) {
        onFlush(segments);
      }
    }, this.flushInterval);
  }

  /**
   * Stop auto-flush timer
   */
  stopAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Reset tracker (for new video)
   */
  reset(): void {
    this.stopAutoFlush();
    this.finalizeCurrentSegment();
    this.segments = [];
    this.currentSegment = null;
    this.lastKnownTime = 0;
    this.isPaused = false;
  }

  /**
   * Get total watched time in seconds
   */
  getTotalWatchedTime(): number {
    const currentSegments = this.getCurrentSegments();
    const currentSegment = this.getCurrentSegmentInfo();
    
    let total = currentSegments.reduce((sum, segment) => sum + segment.duration, 0);
    if (currentSegment) {
      total += currentSegment.duration;
    }
    
    return total;
  }

  /**
   * Get completion percentage
   */
  getCompletionPercentage(videoDuration: number): number {
    if (videoDuration <= 0) return 0;
    return Math.min(100, (this.getTotalWatchedTime() / videoDuration) * 100);
  }
}
